// The agency's top-up (design 1, on « Mon agence » and on « Compte »): an
// amount, MTN MoMo or Orange Money, the paying number, then the PIN on the
// phone. It calls MyGaz Pro's own pay-topup (C18, D-46, D-47), so D-52's rule
// holds: until real money opens, only the sandbox agency can pay.
import { useEffect, useRef, useState } from 'react';
import { S } from '../lib/strings';
import { xaf } from '../lib/format';
import { netName, topupOffers, hm } from '../lib/agences';
import { cmPhone } from '../lib/phone';
import type { AgencySource } from '../lib/agency-api';
import type { Provider, Topup, TopupState } from '../lib/types';
import { Ic } from '../components/Icon';
import { Net, NetText } from '../components/agences/Bits';

function errorText(code: string) {
  const t = S.agency.tu;
  return code === 'payments_off' ? t.off : code === 'invalid_phone' ? t.badPhone : code === 'topup_in_flight' ? t.busy : t.error;
}

export function TopupPanel({ src, providers, min, max, feePerDelivery, balance, phone, pending, onDone }: {
  src: AgencySource; providers: Provider[]; min: number; max: number; feePerDelivery: number; balance: number; phone: string | null;
  pending: Topup | null; onDone(): void;
}) {
  const { offers, pick } = topupOffers(feePerDelivery);
  const [amount, setAmount] = useState<number>(pick);
  const [other, setOther] = useState('');
  const [net, setNet] = useState<Provider>(providers[0] ?? 'MTN_MOMO_CMR');
  const [num, setNum] = useState(phone ? phone.replace(/^237/, '').replace(/(\d)(\d{2})(\d{2})(\d{2})(\d{2})$/, '$1 $2 $3 $4 $5') : '');
  const [state, setState] = useState<TopupState | null>(null);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'bad' | 'wait'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);

  const value = other ? Math.round(Number(other.replace(/[\s\u00a0]/g, ''))) : amount;
  const perDel = Math.max(1, feePerDelivery);
  const valid = Number.isFinite(value) && value >= min && value <= max;

  const follow = (id: string, tries = 0) => {
    timer.current = setTimeout(async () => {
      try {
        const t = await src.topupCheck(id);
        setState(t);
        if (t.status === 'completed') { setMsg({ tone: 'ok', text: S.agency.tu.done(xaf(t.amount_xaf)) }); setBusy(false); onDone(); return; }
        if (t.status === 'failed' || t.status === 'rejected') { setMsg({ tone: 'bad', text: S.agency.tu.failed }); setBusy(false); onDone(); return; }
        if (tries < 40) follow(id, tries + 1); else { setBusy(false); onDone(); }
      } catch { if (tries < 40) follow(id, tries + 1); else setBusy(false); }
    }, tries ? 4000 : 2500);
  };

  const pay = async (e: React.FormEvent) => {
    e.preventDefault();
    const p = cmPhone(num);
    if (!p) { setMsg({ tone: 'bad', text: S.agency.tu.badPhone }); return; }
    if (!valid) { setMsg({ tone: 'bad', text: S.agency.tu.bounds(xaf(min), xaf(max)) }); return; }
    setBusy(true); setMsg(null);
    try {
      const t = await src.topup(value, net, p.replace('+', ''));
      setState(t);
      if (t.status === 'rejected' || t.status === 'failed') { setMsg({ tone: 'bad', text: S.agency.tu.failed }); setBusy(false); return; }
      setMsg({ tone: 'wait', text: S.agency.tu.sent });
      follow(t.topup_id);
    } catch (err) {
      setMsg({ tone: 'bad', text: errorText(String((err as Error)?.message ?? err)) });
      setBusy(false);
    }
  };

  return (
    <form className="ag-tu" onSubmit={pay}>
      {pending && !state && (
        <div className="ag-pend"><Ic n="clock" s="xs" /><span><NetText text={S.agency.tu.waiting(xaf(pending.amount), netName(pending.provider), hm(pending.at))} /></span></div>
      )}
      <h3>{S.agency.tu.title}</h3>
      <span className="cap">{S.agency.tu.amount}</span>
      <div className="ag-amts" role="radiogroup" aria-label={S.agency.tu.amount}>
        {offers.map((o) => (
          <button type="button" key={o} role="radio" aria-checked={!other && amount === o} className={'ag-amt' + (!other && amount === o ? ' on' : '')} onClick={() => { setAmount(o); setOther(''); }}>
            {o.toLocaleString('fr-FR').replace(/[\u202f\u00a0]/g, '\u00a0')}
          </button>
        ))}
        <input className={'ag-amt in' + (other ? ' on' : '')} inputMode="numeric" placeholder={S.agency.tu.other} value={other} onChange={(e) => setOther(e.target.value)} aria-label={S.agency.tu.other} />
      </div>
      <p className="ag-small muted">{valid ? S.agency.tu.about(Math.round(value / perDel), xaf(balance + value)) : S.agency.tu.bounds(xaf(min), xaf(max))}</p>
      <span className="cap">{S.agency.tu.payWith}</span>
      <div className="ag-nets" role="radiogroup" aria-label={S.agency.tu.payWith}>
        {providers.map((p) => (
          <button type="button" key={p} role="radio" aria-checked={net === p} className={'ag-nt' + (net === p ? ' on' : '')} onClick={() => setNet(p)}><Net p={p} s={30} />{netName(p)}</button>
        ))}
      </div>
      <label className="ag-fld"><input inputMode="tel" value={num} onChange={(e) => setNum(e.target.value)} aria-label={S.agency.tu.number} placeholder="6XX XX XX XX" /><span className="muted">{S.agency.tu.number}</span></label>
      <button className="btn pri ag-pay" disabled={busy || !providers.length}>{busy && state ? <Ic n="clock" s="xs" /> : null}{S.agency.tu.pay(xaf(valid ? value : amount))}</button>
      {msg ? <p className={'ag-msg ' + msg.tone} role="status">{msg.text}</p> : <p className="ag-small muted">{src.demo ? S.agency.tu.demo : S.agency.tu.pin}</p>}
    </form>
  );
}
